import { GameConstants, CameraConfig } from '@game/types';
import config from '@/env-config';

// Game constants extracted from main.js for centralized configuration
export const GAME_CONSTANTS: GameConstants = {
  // Player movement
  MOVEMENT_SPEED: 80,
  CAMERA_FOLLOW_LERP: 0.08,
  
  // World and collision
  TILE_SIZE: 16,
  PLAYER_BODY_SIZE: {
    width: 14,  // Physics body width (excludes head area)
    height: 10  // Physics body height (excludes head area)
  },
  
  // Networking
  WEBSOCKET_RECONNECT_ATTEMPTS: 5,
  
  // Pathfinding
  PATHFINDING_STUCK_THRESHOLD: 120  // 2 seconds at 60fps
};

// Camera configuration from GameScene
export const CAMERA_CONFIG: CameraConfig = {
  ZOOM: {
    MIN: 1.5,
    MAX: 7.0,
    STEP: 0.5,
    DEFAULT_ON_JOIN: 5.0  // Balanced zoom when joining room
  },
  FOLLOW: {
    LERP_X: 0.08,
    LERP_Y: 0.08
  },
  TRANSITION: {
    DURATION: 800,
    EASE: 'Back.easeOut'
  }
};

// Animation configuration
export const ANIMATION_CONFIG = {
  IDLE_FRAME_RATE: 2,  // Slower idle animation (2 fps instead of 4)
  RUN_FRAME_RATE: 12,
  DIRECTIONS: ['right', 'up', 'left', 'down'] as const
};

// WebSocket configuration
export const WEBSOCKET_CONFIG = {
  SERVER_URL: config.api.wsUrl,
  RECONNECTION_DELAY: 1000,
  RECONNECTION_DELAY_MAX: 5000,
  TIMEOUT: 10000,
  MOVEMENT_UPDATE_INTERVAL: 100  // 10 times per second
};

// Physics configuration  
export const PHYSICS_CONFIG = {
  PLAYER_BODY_OFFSET: {
    x: 1,   // 1px from left edge of sprite
    y: 22   // 22px from top (positions at feet/torso)
  },
  MOVEMENT_THRESHOLD: 1,  // Minimum distance to trigger movement update
  PATH_WAYPOINT_THRESHOLD: 8,  // Distance to consider waypoint reached
  INTERPOLATION_SPEED: 5  // Remote player position interpolation
};

// UI configuration
export const UI_CONFIG = {
  NAME_TAG: {
    FONT_SIZE: '12px',
    FONT_FAMILY: 'Arial, Helvetica, sans-serif',
    PADDING: 6,
    BACKGROUND_ALPHA: 0.4,
    BORDER_COLOR: 0xcccccc,
    OFFSET_Y: 8  // Gap above player's head
  },
  DEPTHS: {
    PLAYER: 1,
    NAME_TAG: 10,
    UI_OVERLAY: 1000
  }
};

export type AnimationDirection = typeof ANIMATION_CONFIG.DIRECTIONS[number];

/**
 * Convert a position to pixel-perfect coordinates for crisp rendering.
 * For center-origin sprites, position at integer + 0.5 to align edges to pixels.
 * This prevents texture bleeding and ensures sharp rendering.
 */
export function pixelPerfectPosition(value: number): number {
  return Math.round(value * 2) / 2;
}